import React, { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import api from "config/axiosConfig";
import { Meeting, Sort, SortField, SortOrder } from "util/constants";
import { useUser } from "util/UserContext";
import MeetingCard from "./MeetingCard";
import LoadingSkeleton from "ui/LoadingSkeleton";
import ScrollHideContainer from "ui/ScrollHideContainer";
import { SelectTrigger, SelectValue, SelectItem, SelectContent, Select } from "ui/InputSelect";
import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import ServerErrorPage from "components/ResponsePages/500ServerErrorPage";
import AddButton from "ui/AddButton";
import AddMeeting from "./AddMeeting";
import { AxiosError } from "axios";

interface Props {
  initialSort?: Sort;
}

const MeetingsList = ({ initialSort = "soonest" }: Props) => {
  const { currentUser, setShowAuthModal } = useUser();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [error, setError] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isPrivateFilter, setIsPrivateFilter] = useState<"All" | "public" | "private">("All");
  const [timeFilter, setTimeFilter] = useState<"All" | "upcoming" | "past">("upcoming");
  const [sortField, setSortField] = useState<SortField>("start_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Intersection observer for infinite scrolling
  const [loadMoreRef, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  // Initial load ref
  const [initialLoadRef, initialInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const LIMIT = 10;

  useEffect(() => {
    switch (initialSort) {
      case "top":
        setSortField("participant_count");
        setSortOrder("desc");
        break;
      case "new":
        setSortField("created_at");
        setSortOrder("desc");
        break;
      case "soonest":
        setSortField("start_at");
        setSortOrder("asc");
        break;
      case "latest":
        setSortField("start_at");
        setSortOrder("desc");
        break;
      default:
        setSortField("start_at");
        setSortOrder("asc");
        break;
    }
  }, [initialSort]);

  const fetchMeetings = async (page: number, isInitialLoad: boolean = false) => {
    if (isInitialLoad) {
      setIsLoading(true);
    }

    try {
      const offset = (page - 1) * LIMIT;
      const orderBy = sortField === "start_at" ? "start_at" : sortField === "created_at" ? "created_at" : "created_at";
      const orderDir = sortOrder;

      const response = await api.get<{ meetings: Meeting[]; total: number }>("/api/meetings", {
        params: {
          limit: LIMIT,
          offset,
          orderBy,
          orderDir,
        },
      });

      const newMeetings = response.data.meetings || [];
      setTotalCount(response.data.total || 0);
      
      if (isInitialLoad || page === 1) {
        setMeetings(newMeetings);
      } else {
        setMeetings(prevMeetings => [...prevMeetings, ...newMeetings]);
      }
      
      setHasMore(newMeetings.length === LIMIT);
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 404) {
        setError("404: Failed to load events. Please try again later.");
      } else {
        setError("Failed to load events. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Reset pagination when filters change
    setPageNumber(1);
    setMeetings([]);
    if (initialInView) {
      fetchMeetings(1, true);
    }
    //eslint-disable-next-line
  }, [sortField, sortOrder, initialInView]);

  useEffect(() => {
    if (inView && hasMore && !isLoading && pageNumber > 1) {
      fetchMeetings(pageNumber);
    }
    //eslint-disable-next-line
  }, [inView, hasMore, isLoading, pageNumber]);

  const loadMoreMeetings = () => {
    if (hasMore && !isLoading) {
      setPageNumber(prevPage => prevPage + 1);
    }
  };

  useEffect(() => {
    if (inView && hasMore && !isLoading) {
      loadMoreMeetings();
    }
    //eslint-disable-next-line
  }, [inView, hasMore, isLoading]);

  const handleSortChange = (newSort: Sort) => {
    switch (newSort) {
      case "top":
        setSortField("participant_count");
        setSortOrder("desc");
        break;
      case "new":
        setSortField("created_at");
        setSortOrder("desc");
        break;
      case "soonest":
        setSortField("start_at");
        setSortOrder("asc");
        break;
      case "latest":
        setSortField("start_at");
        setSortOrder("desc");
        break;
    }
  };

  const handleAddMeetingButtonClick = () => {
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleMeetingAdded = async () => {
    try {
      setPageNumber(1);
      await fetchMeetings(1, true);
    } catch (err) {
      setError("Failed to refresh events. Please reload the page.");
    }
  };

  //TODO: use totalCount
  console.log(totalCount);

  // Filter meetings
  const filteredMeetings = meetings.filter((meeting) => {
    // Filter by privacy
    if (isPrivateFilter !== "All") {
      const isPrivate = meeting?.properties?.is_private || false;
      if (isPrivateFilter === "private" && !isPrivate) return false;
      if (isPrivateFilter === "public" && isPrivate) return false;
    }

    // Filter by time
    if (timeFilter !== "All") {
      const now = new Date();
      const startDate = meeting?.properties?.start_at ? new Date(meeting.properties.start_at) : null;
      
      if (timeFilter === "upcoming" && startDate && startDate < now) return false;
      if (timeFilter === "past" && (!startDate || startDate >= now)) return false;
    }

    return true;
  });

  if (error) {
    return error.match(/404/) ? 
      <NotFoundPage 
        displayStyle="page" 
        errorMessage="Sorry, we couldn't find any events." 
        buttonText="Go to Home" 
        buttonHref="/" 
      /> : 
      <ServerErrorPage />;
  }

  return (
    <div className="meetings-list" ref={initialLoadRef}>
      <ScrollHideContainer 
        className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
        hideOffset={115}
        threshold={114}
      >
        <div className="flex flex-col space-y-1 mb-1">
            <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                Under development - may not function properly.
            </div>
          <div className="flex mb-1">
            <AddButton
              tooltipTitle="Add Event"
              buttonText="Event"
              className="w-full"
              onClick={handleAddMeetingButtonClick}
            />
          </div>
          <div className="flex gap-1 pb-1">
            <Select onValueChange={(value: Sort) => handleSortChange(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="soonest">Soonest</SelectItem>
                <SelectItem value="latest">Latest</SelectItem>
                <SelectItem value="top">Most Popular</SelectItem>
                <SelectItem value="new">Newest Added</SelectItem>
              </SelectContent>
            </Select>
            <Select onValueChange={(value) => setTimeFilter(value as "All" | "upcoming" | "past")}>
              <SelectTrigger>
                <SelectValue placeholder="Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Events</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="past">Past</SelectItem>
              </SelectContent>
            </Select>
            <Select onValueChange={(value) => setIsPrivateFilter(value as "All" | "public" | "private")}>
              <SelectTrigger>
                <SelectValue placeholder="Privacy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Events</SelectItem>
                <SelectItem value="public">Public Only</SelectItem>
                <SelectItem value="private">Private Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </ScrollHideContainer>
      
      <div className="mt-0">
        {isLoading && pageNumber === 1 ? (
          <LoadingSkeleton count={3} />
        ) : filteredMeetings.length === 0 ? (
          <p className="m-4 text-sm">Be the first to add an event!</p>
        ) : (
            <div className="entity-divider flex flex-col">
            {filteredMeetings.map((meeting) => (
              <div key={meeting.id}>
                <MeetingCard
                    key={meeting.id}
                    meeting={meeting}
                    truncateDescription={true}
                    isMeetingProfilePage={false}
                    isEntitySection={false}
                />
              </div>
            ))}
            {hasMore && (
              <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
                {isLoading && pageNumber > 1 && <LoadingSkeleton count={1} />}
              </div>
            )}
          </div>
        )}
      </div>
      
      {openModal && (
        <AddMeeting
          onMeetingAdded={handleMeetingAdded}
          open={openModal}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default MeetingsList;
